<script lang="ts" setup>
import { onMounted, ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useProductStore } from '@/stores/product-store'
import { useTransactionStore } from '@/stores/transaction-store'
import { TransactionImageType } from '@/types/transaction'
import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import { PackagingPreferences } from '@/types/packaging'
import { useCompanyStore } from '@/stores/company-store'

import ManageTransactionImageItem from './ManageTransactionImageItem.vue'
import ManageTransactionProductItem from './ManageTransactionProductItem.vue'

import MyFileSelect from '@/components/my-components/MyFileSelect.vue'
import MyFormWizardStep from '@/components/my-components/form/MyFormWizardStep.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'

export interface Props {
    active: boolean
    imageUploadOpen: boolean
}

const { t } = useI18n()

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'closeImageUpload'): void }>()
const productStore = useProductStore()
const transactionStore = useTransactionStore()
const authStore = useAuthStore()
const companyStore = useCompanyStore()

const errorMessage = ref('')
const showAllProducts = ref(false)
const transaction = transactionStore.transaction!
const receivedImages = computed(() =>
    transaction.images.filter((image) => image.type === TransactionImageType.Received),
)
const deliveredImages = computed(() =>
    transaction.images.filter((image) => image.type === TransactionImageType.Delivered),
)
const uploadingImages = computed(
    () =>
        transaction.images.filter((image) => !image.path).length !== 0 &&
        transaction.images.length > 0,
)
const nilTradesEnabled = computed(() => {
    const license = authStore.licenses[LicenseType.PackagingModule] as
        | PackagingPreferences
        | undefined

    return license?.nilTradesEnabled === true
})

function handleImageUpload(images: File[], type: TransactionImageType) {
    const transactionImages = images.map((image) => ({ file: image, type, note: '' }))
    transactionStore.appendImages(transactionImages)
}

function submit() {
    if (uploadingImages.value) {
        errorMessage.value = t('uploadingImagesError')
        return false
    }

    const hasProducts = transaction.products.some(
        (product) => product.received > 0 || product.delivered > 0,
    )
    if (!hasProducts && !nilTradesEnabled.value) {
        errorMessage.value = t('manageTransactionProductsError')
        return false
    }

    errorMessage.value = ''

    return true
}

onMounted(async () => {
    if (!productStore.loaded) await productStore.fetchProducts()
    await companyStore.fetchCompany()

    transaction.products = productStore.transactionProducts(
        transactionStore.isCreatingNew ? [] : transaction.products,
        showAllProducts.value,
    )
})

watch(showAllProducts, () => {
    transaction.products = productStore.transactionProducts(
        transaction.products,
        showAllProducts.value,
    )
})
</script>

<template>
    <MyFormWizardStep
        id="products"
        :active="props.active"
        :submit="submit"
        :title="t('products')"
        :loading="uploadingImages"
    >
        <div class="flex justify-end text-center text-xs">
            <div
                v-if="errorMessage"
                class="grow text-left font-semibold text-red-500"
                v-text="errorMessage"
            />

            <div class="mb-1 flex items-center gap-1">
                <MyInputLabel v-text="t('delivered')" />
                <mdi:information v-tooltip="t('transactionSenderProductExplanation')" />
            </div>

            <div class="mb-1 flex items-center gap-1">
                <MyInputLabel v-text="t('received')" />
                <mdi:information v-tooltip="t('transactionReceiverProductExplanation')" />
            </div>
        </div>
        <div class="mt-3">
            <ManageTransactionProductItem
                v-for="(product, index) in transaction.products"
                :key="product.id"
                v-model="transaction.products[index]"
                @update:model-value="errorMessage = ''"
            />
        </div>
        <div class="flex items-center space-x-1">
            <MyCheckbox v-model="showAllProducts" class="mt-4" :label="t('showAllProducts')" />
            <mdi:information v-tooltip="t('showAllProductsExplanation')" class="self-end" />
        </div>

        <div v-if="imageUploadOpen || transaction.images.length !== 0" class="relative mt-6">
            <button
                v-if="imageUploadOpen"
                type="button"
                class="absolute top-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-yellow-400 text-center text-sm transition-all hover:bg-yellow-500"
                @click="emit('closeImageUpload')"
            >
                <mdi:close />
            </button>

            <div class="grid grid-cols-2 gap-6">
                <div>
                    <template v-if="imageUploadOpen || deliveredImages.length !== 0">
                        <h2
                            class="my-3 text-center font-semibold uppercase"
                            v-text="t('deliveredImages')"
                        />
                        <MyFileSelect
                            v-if="imageUploadOpen"
                            multiple
                            class="h-40 rounded-xl"
                            @selected:multiple="
                                (img) => handleImageUpload(img, TransactionImageType.Delivered)
                            "
                        />
                        <div
                            v-if="deliveredImages.length !== 0"
                            class="mt-3 grid grid-cols-3 gap-3 rounded-xl"
                        >
                            <ManageTransactionImageItem
                                v-for="(image, index) in deliveredImages"
                                :key="'delivered-' + index"
                                :image="image"
                            />
                        </div>
                    </template>
                </div>
                <div>
                    <template v-if="imageUploadOpen || receivedImages.length !== 0">
                        <h2
                            class="my-3 text-center font-semibold uppercase"
                            v-text="t('receivedImages')"
                        />
                        <MyFileSelect
                            v-if="imageUploadOpen"
                            multiple
                            class="h-40 rounded-xl"
                            @selected:multiple="
                                (img) => handleImageUpload(img, TransactionImageType.Received)
                            "
                        />
                        <div
                            v-if="receivedImages.length !== 0"
                            class="mt-3 grid grid-cols-3 gap-3 rounded-xl"
                        >
                            <ManageTransactionImageItem
                                v-for="(image, index) in receivedImages"
                                :key="'received-' + index"
                                :image="image"
                            />
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </MyFormWizardStep>
</template>
